import { type PaymentBreakdown } from '../../types/PaymentBreakdown';
import { type ShippingMethod, type ShippingContact } from '../../types/Shipping';
import { getLocalizeBreakdownLabel } from '../../utils/i18n/paymentBreakdown';
import { type BillingContact } from '../../types/BillingContact';
import { type OrderItem } from '../../types/OrderItem';
import { getFeeItems, getProductItems } from '../../utils/feeItems';
import { getOrderItemsTotal, formatAmount } from '../../components/Widget/utils/amount';

export function transformPaymentBreakdownToApplePay(
  paymentBreakdown: PaymentBreakdown,
  orderItems?: OrderItem[],
): ApplePayJS.ApplePayLineItem[] {
  if (!paymentBreakdown) {
    return [];
  }

  const feeItems = getFeeItems(orderItems);
  const productItems = getProductItems(orderItems);
  const productItemsTotal = getOrderItemsTotal(productItems);

  const lineItems: ApplePayJS.ApplePayLineItem[] = [];

  lineItems.push({
    label: getLocalizeBreakdownLabel('itemsTotal'),
    amount: formatAmount(productItemsTotal),
  });

  Object.entries(paymentBreakdown).forEach(([type, amount]) => {
    if (amount) {
      lineItems.push(applePayLineItemBuilder(type as keyof PaymentBreakdown, amount));
    }
  });

  // Add fee items as separate line items
  const feeLineItems = feeItems.map((feeItem) => ({
    label: feeItem.name,
    amount: formatAmount(Number(feeItem.price) * feeItem.quantity),
  }));

  return [...lineItems, ...feeLineItems];
}

export function transformShippingMethodToCommon(shippingMethod: ApplePayJS.ApplePayShippingMethod): ShippingMethod {
  return shippingMethod;
}

export const getRestrictedContact = (
  contact: ApplePayJS.ApplePayPaymentContact,
): {
  countryCode: string;
  city: string;
  state: string;
  postalCode: string;
} => {
  return {
    countryCode: contact.countryCode?.toUpperCase(),
    state: contact.administrativeArea,
    city: contact.locality,
    postalCode: contact.postalCode,
  };
};

export const getBillingContact = (
  billing: ApplePayJS.ApplePayPaymentContact,
  shipping: ApplePayJS.ApplePayPaymentContact,
): BillingContact => {
  return {
    countryCode: billing.countryCode?.toUpperCase(),
    email: shipping.emailAddress,
    phone: billing.phoneNumber,
    firstName: billing.givenName,
    lastName: billing.familyName,
  };
};

export const getShippingContact = (shipping: ApplePayJS.ApplePayPaymentContact): ShippingContact => {
  return {
    firstName: shipping.givenName,
    lastName: shipping.familyName,
    countryCode: shipping.countryCode?.toUpperCase(),
    phone: shipping.phoneNumber,
    postalCode: shipping.postalCode,
    state: shipping.administrativeArea,
    city: shipping.locality,
    addressLine1: Array.isArray(shipping.addressLines) && shipping.addressLines[0],
    addressLine2: Array.isArray(shipping.addressLines) && shipping.addressLines[1],
  };
};

function applePayLineItemBuilder(type: keyof PaymentBreakdown, value: string): ApplePayJS.ApplePayLineItem {
  const label = getLocalizeBreakdownLabel(type);
  const amount = type === 'discount' ? `-${value}` : value;
  return {
    label,
    amount,
  };
}
